import LegalLayout, { LegalSection } from '@/components/LegalLayout';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/SeoHead';

export default function CookiePolicy() {
  return (
    <>
      <SeoHead
        title="Cookie Policy"
        description="Information about the cookies WriteAssess uses and how to manage them."
        canonical="/cookie-policy"
      />
      <LegalLayout title="Cookie Policy" lastUpdated="24 April 2026">
      <LegalSection title="1. What Are Cookies">
        <p>
          Cookies are small text files placed on your device when you visit a website. They allow the site to
          recognise your browser and remember certain information across pages or visits.
        </p>
      </LegalSection>

      <LegalSection title="2. How We Use Cookies">
        <p>
          WriteAssess uses a minimal number of cookies, all of which are strictly necessary for the platform to
          function. We do not use advertising cookies, tracking pixels, or third-party analytics cookies that
          monitor your behaviour across other websites.
        </p>
      </LegalSection>

      <LegalSection title="3. Cookies We Set">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 pr-4 font-semibold text-foreground">Cookie</th>
              <th className="text-left py-2 pr-4 font-semibold text-foreground">Purpose</th>
              <th className="text-left py-2 pr-4 font-semibold text-foreground">Type</th>
              <th className="text-left py-2 font-semibold text-foreground">Expiry</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {[
              ['sb-access-token', 'Maintains your authenticated session with Supabase', 'Strictly necessary', '1 hour'],
              ['sb-refresh-token', 'Allows your session to be renewed without re-login', 'Strictly necessary', '60 days'],
              ['sb-auth-token', 'Stores authentication state for the current session', 'Strictly necessary', 'Session'],
            ].map(([name, purpose, type, expiry]) => (
              <tr key={name}>
                <td className="py-2 pr-4 font-mono text-xs text-foreground">{name}</td>
                <td className="py-2 pr-4">{purpose}</td>
                <td className="py-2 pr-4">{type}</td>
                <td className="py-2">{expiry}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>
          These cookies are set by our authentication provider (Supabase) and are essential for you to remain
          logged in and access protected areas of the platform.
        </p>
      </LegalSection>

      <LegalSection title="4. Third-Party Cookies">
        <p>
          When you visit our payment pages, Stripe may set cookies on your device to detect fraud and ensure
          payment security. These cookies are governed by{' '}
          <a
            href="https://stripe.com/en-gb/cookie-settings"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Stripe's Cookie Policy
          </a>.
        </p>
        <p>
          We do not use Google Analytics, Facebook Pixel, or any other third-party advertising or tracking
          services.
        </p>
      </LegalSection>

      <LegalSection title="5. Local Storage">
        <p>
          In addition to cookies, WriteAssess uses browser local storage to preserve your preferences (such as
          sidebar state) between sessions. This data never leaves your device and is not transmitted to our servers.
        </p>
      </LegalSection>

      <LegalSection title="6. Managing Cookies">
        <p>
          Because we only use strictly necessary cookies, we do not present a cookie consent banner — under UK
          PECR, consent is not required for cookies that are essential to a service you have requested.
        </p>
        <p>
          You can delete cookies at any time via your browser settings. Note that deleting authentication cookies
          will log you out of WriteAssess. Instructions for common browsers:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Google Chrome
            </a>
          </li>
          <li>
            <a href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Mozilla Firefox
            </a>
          </li>
          <li>
            <a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Apple Safari
            </a>
          </li>
          <li>
            <a href="https://support.microsoft.com/en-us/windows/manage-cookies-in-microsoft-edge" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Microsoft Edge
            </a>
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="7. Changes to This Policy">
        <p>
          We may update this Cookie Policy if we introduce new cookies or change how we use existing ones. The date
          at the top of this page shows when it was last updated. Material changes will be notified via the platform.
        </p>
      </LegalSection>

      <LegalSection title="8. Contact">
        <p>
          Questions about our use of cookies? Contact us at{' '}
          <Link to="/contact" className="text-primary hover:underline">our contact form</Link>. For more information about cookies generally, visit{' '}
          <a href="https://ico.org.uk/for-the-public/online/cookies" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            ico.org.uk
          </a>.
        </p>
      </LegalSection>

      <div className="text-sm text-muted-foreground pt-4 border-t">
        <p>
          See also:{' '}
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
          {' · '}
          <Link to="/data-protection" className="text-primary hover:underline">Data Protection Policy</Link>
        </p>
      </div>
    </LegalLayout>
    </>
  );
}
